/**
 * Normalise un numéro au format canonique (chiffres E.164 sans +).
 * Source de vérité pour WhatsApp, matching clients / ramassage, et stockage CF.
 * Gère notamment les mobiles camerounais saisis en local (6XXXXXXXX ou 06XXXXXXXX).
 */
export function normalizePhoneForWhatsApp(input: string): string | null {
  if (!input || typeof input !== 'string') return null;
  let digits = input.replace(/\D/g, '');
  if (!digits) return null;

  // Cameroun : mobile 9 chiffres sans indicatif (commence par 6)
  if (digits.length === 9 && /^6[0-9]{8}$/.test(digits)) {
    digits = `237${digits}`;
  }
  // Cameroun : format local avec 0 initial
  if (digits.length === 10 && digits.startsWith('0') && /^06[0-9]{8}$/.test(digits)) {
    digits = `237${digits.slice(1)}`;
  }

  // E.164 : 8 à 15 chiffres, pas de 0 en tête (indicatif pays)
  if (digits.length < 8 || digits.length > 15) return null;
  if (digits.startsWith('0')) return null;

  return digits;
}

/** Mobile Cameroun valide : 237 + 9 chiffres dont le premier est 6. */
export function isValidCameroonMobilePhone(input: string | undefined | null): boolean {
  if (input === undefined || input === null) return false;
  const canonical = normalizePhoneForWhatsApp(String(input));
  return Boolean(canonical && /^2376\d{8}$/.test(canonical));
}

/**
 * Masque de saisie Cameroun (affichage uniquement).
 * Accepte 6… / 06… / 237… / +237… et formate en `6XX XXX XXX` ou `+237 6XX XXX XXX`.
 */
export function formatCameroonPhoneInput(input: string): string {
  if (!input || typeof input !== 'string') return '';
  const hadPlus = input.trimStart().startsWith('+');
  let digits = input.replace(/\D/g, '');
  if (!digits) return hadPlus ? '+' : '';

  const group3 = (s: string) => s.replace(/(\d{3})(?=\d)/g, '$1 ').trim();

  // Mode international : +… ou 237…
  if (digits.startsWith('237') || hadPlus) {
    const national = digits.startsWith('237')
      ? digits.slice(3).slice(0, 9)
      : digits.slice(0, 9);
    if (!national) return '+237';
    return `+237 ${group3(national)}`.trim();
  }

  // 0 seul pendant la frappe
  if (digits === '0') return '0';

  // Local 0XXXXXXXXX → afficher les 9 chiffres nationaux
  if (digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  digits = digits.slice(0, 9);
  return group3(digits);
}

/** Alias métier : même canonique que WhatsApp / ramassage. */
export function normalizePhoneCanonical(input: string): string | null {
  return normalizePhoneForWhatsApp(input);
}

/**
 * Compare deux numéros après normalisation canonique.
 * Couvre 670… / 0670… / +237670… / 237670… pour le Cameroun.
 */
export function phonesMatch(a?: string | null, b?: string | null): boolean {
  const na = normalizePhoneForWhatsApp(String(a || ''));
  const nb = normalizePhoneForWhatsApp(String(b || ''));
  if (na && nb) return na === nb;
  const da = String(a || '').replace(/\D/g, '');
  const db = String(b || '').replace(/\D/g, '');
  return Boolean(da && db && da === db);
}

/**
 * Variantes d’écriture à tester en requête Firestore (clients / demandes legacy).
 * Max ~6 valeurs — compatible avec `in` (limite 30).
 */
export function buildPhoneLookupVariants(input: string): string[] {
  const set = new Set<string>();
  const raw = String(input || '').trim();
  const digits = raw.replace(/\D/g, '');
  const canonical = normalizePhoneForWhatsApp(raw);

  if (raw) set.add(raw);
  if (digits) set.add(digits);
  if (canonical) {
    set.add(canonical);
    set.add(`+${canonical}`);
    if (canonical.startsWith('237') && canonical.length === 12) {
      const local9 = canonical.slice(3);
      set.add(local9);
      set.add(`0${local9}`);
    }
  }

  return Array.from(set).filter(Boolean).slice(0, 10);
}

export function isValidWhatsAppPhone(phone: string | undefined): boolean {
  if (phone === undefined || phone === null) return false;
  return normalizePhoneForWhatsApp(phone) !== null;
}

/** URL https://wa.me/... — ouvre l’app sur mobile et WhatsApp Web sur bureau si connecté */
export function getWhatsAppMeUrl(phone: string): string | null {
  const digits = normalizePhoneForWhatsApp(phone);
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}

/** URL https://wa.me/...?text= — message prérempli */
export function getWhatsAppMeUrlWithText(phone: string, text: string): string | null {
  const digits = normalizePhoneForWhatsApp(phone);
  if (!digits) return null;
  const q = text.trim() ? `?text=${encodeURIComponent(text.trim())}` : '';
  return `https://wa.me/${digits}${q}`;
}

/** URL web.whatsapp.com/send?... — force l'ouverture WhatsApp Web sur desktop */
export function getWhatsAppWebSendUrl(phone: string, text: string): string | null {
  const digits = normalizePhoneForWhatsApp(phone);
  if (!digits) return null;
  return `https://web.whatsapp.com/send?phone=${digits}&text=${encodeURIComponent(text)}`;
}
